import { useMemo } from 'react';
import { useObserver } from 'mobx-react';
import { DetailsDefaultValues } from '../types/IDetailsFormData';
import { getStateName } from '../../../../domain/united-states/domain/use-cases/getStateName';
import { getStateNameForAbbreviation } from '../../../../domain/united-states/domain/use-cases/getStateNameForAbbreviation';
import { formatInternationalPhoneNumber } from '../helpers/formatInternationalPhoneNumber';
import { useUserStore } from '../../../../../auth/user/domain/stores/useUserStore';
import { IBooking } from '../../../../domain/booking/models/IBooking';
import { ReasonForTravel } from '../../../../domain/booking/models/ITravelDetails';
import { NewBooking } from '../../../../domain/booking/models/NewBooking';
import { useNewBookingStore } from '../../../../domain/booking/stores/useNewBookingsStore';
import { useVehicleStores } from '../../../../../account/vehicles/domain/stores/useVehicleStores';
import { useAuthStore } from '../../../../../auth/shared/store/useAuthStore';

export const COMMON_DEFAULT_VALUES = {
	reasonForTravel: ReasonForTravel.Business,
	state: '',
	saveVehicle: false,
	// `shouldCreateNewUserAccount` is intentionally not set here. It must be
	// derived from auth state at the call site: `true` (pre-selected) for
	// guests, `false` for authenticated users. Setting it to `true`
	// unconditionally would require the password fields to be filled even when
	// the "create account" section is hidden, blocking submission.
	shouldCreateNewUserAccount: false,
};

export function useNewBookingDefaultValues(): DetailsDefaultValues {
	const userStore = useUserStore();
	const { getVehicleStore } = useVehicleStores();
	const authStore = useAuthStore();

	const newBookingStore = useNewBookingStore();

	const user = useObserver(() => userStore.user);
	const isAuthenticated = useObserver(() => authStore.isAuthenticated);
	const vehicleListLength = useObserver(
		() => getVehicleStore.value?.length ?? 0,
	);
	const hasStoredVehicles = vehicleListLength > 0;

	const bookingDefaultValues = getDefaultValuesForBooking(
		newBookingStore.booking,
		hasStoredVehicles,
	);
	const vehicles = getVehicleStore.value || [];
	const initialFavoriteVehicle =
		vehicles.find((vehicle) => vehicle.favorite) || null;

	return useMemo(() => {
		const shouldCreateNewUserAccount = !isAuthenticated;

		if (bookingDefaultValues) {
			return {
				...bookingDefaultValues,
				shouldCreateNewUserAccount,
			};
		}

		if (!user) {
			return { ...COMMON_DEFAULT_VALUES, shouldCreateNewUserAccount };
		}

		const defaultValues: DetailsDefaultValues = {
			...COMMON_DEFAULT_VALUES,
			shouldCreateNewUserAccount,
			...user,
			licensePlateNumber: initialFavoriteVehicle?.licensePlate || '',
			confirmLicensePlateNumber: initialFavoriteVehicle?.licensePlate || '',
			phoneNumber:
				user.phoneNumber && formatInternationalPhoneNumber(user.phoneNumber),
			state: user.state && getStateNameForAbbreviation(user.state),
		};

		return defaultValues;
	}, [bookingDefaultValues, user, initialFavoriteVehicle, isAuthenticated]);
}

export function getDefaultValuesForBooking(
	booking: IBooking | NewBooking,
	hasStoredVehicles?: boolean,
): DetailsDefaultValues | undefined {
	if (!booking || !booking.personalDetails) {
		return undefined;
	}

	const { personalDetails, travelDetails, vehicleDetails } = booking;

	return {
		...COMMON_DEFAULT_VALUES,
		...personalDetails,
		state: getStateName(personalDetails.state),
		reasonForTravel: travelDetails?.reasonForTravel,
		...(hasStoredVehicles
			? {}
			: {
					licensePlateNumber: vehicleDetails?.licensePlateNumber,
					confirmLicensePlateNumber: vehicleDetails?.licensePlateNumber,
			  }),

		licensePlateNumber: vehicleDetails?.licensePlateNumber,
		confirmLicensePlateNumber: vehicleDetails?.licensePlateNumber,
		saveVehicle: vehicleDetails?.saveVehicle,
		termsAndConditions: true,
	};
}
